/**
 * Storage Utility Module
 * Handles all localStorage operations for StudyMeneer
 *
 * ES module. Loaded via <script type="module">, so it is deferred and evaluates
 * after every classic script.
 *
 * ONLY `window.Storage` is shimmed, and that is deliberate. As a classic script
 * this file put ~67 function declarations on the global object, NINE of which
 * collide with functions of the same name in later-loading files:
 *
 *   addTask, loadSettings, exportData, importData, completeTask, deleteTask  → app.js
 *   loadNotes                                                                → notes.js
 *   deleteEvent                                                              → schedule.js
 *   loadCustomPresets                                                        → timer.js
 *
 * Those files load after this one, so their versions already win, and every
 * unqualified call in the codebase — including the onclick="deleteTask(...)"
 * handlers — has always resolved to them. Removing this file's globals changes
 * nothing except making that resolution deterministic instead of load-order luck.
 *
 * DO NOT re-publish the individual functions on window. A module is deferred and
 * evaluates LAST, so `window.deleteTask = deleteTask` here would invert all nine
 * collisions and silently break the UI wrappers in app.js.
 *
 * Callers reach this module through `Storage.x()`, which is unaffected.
 */

// Storage keys
export const STORAGE_KEYS = {
    NOTES: 'productivity_notes',
    // Quarantine slot for notes that failed to parse. Holds the raw unreadable
    // string so it is never destroyed on a parse failure — see loadNotes().
    NOTES_CORRUPT: 'productivity_notes__corrupt',
    DECKS: 'productivity_decks',
    QUIZZES: 'productivity_quizzes',
    CUSTOM_QUIZZES: 'productivity_custom_quizzes',
    QUIZ_RESULTS: 'productivity_quiz_results',
    MISTAKE_LOG: 'productivity_mistake_log',
    TASKS: 'productivity_tasks',
    TIMERS: 'productivity_timers',
    TIMER_SESSIONS: 'productivity_timer_sessions',
    CUSTOM_PRESETS: 'productivity_custom_presets',
    SETTINGS: 'productivity_settings',
    STATS: 'productivity_stats',
    STUDY_STREAK: 'productivity_streak',
    STUDY_TIME: 'productivity_study_time',
    // Task 13/15 releases read this key. Current automatic replay must never use
    // it; it is retained only for explicit, locked recovery and Clear All.
    PENDING_QUIZ_REQUESTS: 'productivity_pending_quiz_requests',
    PENDING_QUIZ_REQUESTS_V2: 'productivity_pending_quiz_requests_v2',
    PENDING_QUIZ_OWNERS: 'productivity_pending_quiz_owners',
    PENDING_QUIZ_REPLAY_RECEIPTS: 'productivity_pending_quiz_replay_receipts',
    EVENTS: 'productivity_events'
};

// Removed Task 13 protocol. Keep this key clearable so old browsers cannot
// leave opaque coordination debris behind after a student chooses Clear All.
const LEGACY_STORAGE_KEYS = ['productivity_pending_quiz_replay_leases'];
const PENDING_QUIZ_QUEUE_LOCK_NAME = 'studymeneer:pending-quiz-queue-drain';

// ── Storage quota guard ───────────────────────────────────────────────────────
// Warn the user when localStorage is getting full (~80% of the ~5M-character limit).
// Browsers cap localStorage in CHARACTERS (key + value), so this counts characters.
// It used to count UTF-16 bytes (x2) against 4 MB, which warned at ~40% full and
// contradicted the percentage Settings shows (QA SET-1).
const _QUOTA_WARN_CHARS = 4 * 1000 * 1000;
let _quotaWarnShown = false;

function _checkQuota() {
    if (_quotaWarnShown) return;
    try {
        let total = 0;
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            total += key.length + (localStorage.getItem(key) || '').length;
        }
        if (total >= _QUOTA_WARN_CHARS) {
            _quotaWarnShown = true;
            if (typeof showNotification === 'function') {
                showNotification('Storage is almost full. Export your data to avoid losing anything.', 'warning');
            } else {
                console.warn('StudyMeneer: localStorage is nearly full (' + Math.round(total / 1000) + 'K characters used)');
            }
        }
    } catch (_) {}
}

// ── Schema validation (roadmap Phase 1) ───────────────────────────────────────
// Stored JSON is not trusted just because it parsed. A value that is valid JSON but
// the wrong shape — an object where a list belongs, or a list holding nulls — used
// to reach callers and throw inside a .map(), which surfaces as a blank screen far
// from the real cause.
//
// This drops what cannot be used and keeps everything else. It never throws and
// never clears storage: salvaging four of five records beats losing all five, and
// deleting a student's data on a validation failure is exactly the mistake
// loadNotes() used to make.
function _asList(value, label) {
    if (Array.isArray(value)) {
        const clean = value.filter(item => item && typeof item === 'object' && !Array.isArray(item));
        if (clean.length !== value.length) {
            console.warn(`[Storage] ${label}: dropped ${value.length - clean.length} malformed record(s).`);
        }
        return clean;
    }
    if (value == null) return [];
    console.warn(`[Storage] ${label}: expected a list, got ${typeof value}. Treating as empty.`);
    return [];
}

// ── Change announcements ──────────────────────────────────────────────────────
// A write that succeeded must never be reported as failed because something
// listening for it threw. EventBus already guards individual listeners, but the
// emit call itself sat inside the same try/catch as localStorage.setItem, so a
// broken bus turned a completed save into `return false` — and the caller then
// warns the student their work did not save when it did.
function _announce(event, payload) {
    try {
        window.EventBus?.emit(event, payload);
    } catch (err) {
        console.error(`[Storage] listener for "${event}" threw; the save itself was fine:`, err);
    }
}

// ── Cloud sync helper ─────────────────────────────────────────────────────────
// Fire-and-forget hand-off. CloudSync.push() resolves the session locally,
// records the write in its dirty ledger and retries it on reconnect, so there is
// nothing to resolve or catch here.
//
// This used to resolve the user itself via the NETWORKED getUser(), which meant
// an offline write never reached a push and was never recorded as pending — and
// the next pull then overwrote it with the older cloud copy.
//
// The optional call is deliberate: a browser holding a cached older cloud-sync.js
// has no push(), and a hard failure here would take the student's save down with
// the sync.
function _cloudPush(collection, data) {
    try { window.CloudSync?.push?.(collection, data); } catch (_) {}
}

/**
 * Notes Storage Functions
 */
function saveNotes(notes) {
    try {
        localStorage.setItem(STORAGE_KEYS.NOTES, JSON.stringify(notes));
        _checkQuota();
        _cloudPush('notes', notes);
        _announce('notes:changed', { notes });
        return true;
    } catch (error) {
        if (error.name === 'QuotaExceededError') {
            if (typeof showNotification === 'function') {
                showNotification('Storage full! Please export and delete some data before saving.', 'error');
            }
        }
        console.error('Error saving notes:', error);
        return false;
    }
}

function loadNotes() {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.NOTES);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) throw new Error('Notes data is not an array');
        return parsed;
    } catch (error) {
        console.error('[Storage] loadNotes corruption detected:', error);

        // Quarantine before clearing. A parse failure is not permission to delete
        // a student's only copy — the raw string is the sole remaining artefact
        // and may still be recoverable by hand. Only clear NOTES once the copy is
        // safely written; if quarantining fails, leave the original in place.
        let quarantined = false;
        try {
            const corrupt = localStorage.getItem(STORAGE_KEYS.NOTES);
            if (corrupt) {
                localStorage.setItem(STORAGE_KEYS.NOTES_CORRUPT, corrupt);
                quarantined = true;
            }
        } catch (e) {
            console.error('[Storage] Could not quarantine corrupt notes — leaving them in place:', e);
        }
        if (quarantined) {
            try { localStorage.removeItem(STORAGE_KEYS.NOTES); } catch (_) {}
        }

        if (window.CloudSync && typeof window.CloudSync.restoreNotes === 'function') {
            console.warn('[Storage] Attempting cloud restore of notes.');
            window.CloudSync.restoreNotes();
        } else if (typeof showNotification === 'function') {
            showNotification(
                quarantined
                    ? 'Your notes could not be read. The unreadable copy has been kept, so do not clear your browser data.'
                    : 'Your notes could not be read. Do not clear your browser data: the original is still on this device.',
                'error'
            );
        }
        return [];
    }
}

/**
 * Flashcard Storage Functions
 * Flashcards are stored inside each note object as note.flashcards = [].
 */

/**
 * Save flashcards for a note (overwrites existing array).
 * @param {string} noteId
 * @param {Array} flashcardsArray
 * @returns {boolean}
 */
function saveFlashcards(noteId, flashcardsArray) {
    try {
        const notes = Storage.loadNotes();
        if (!Array.isArray(notes)) return false;
        const idx = notes.findIndex(n => n.id === noteId);
        if (idx === -1) return false;
        notes[idx].flashcards = flashcardsArray;
        return saveNotes(notes);
    } catch (error) {
        console.error('Error saving flashcards:', error);
        return false;
    }
}

/**
 * Get flashcards for a note, or [] if none.
 * @param {string} noteId
 * @returns {Array}
 */
function getFlashcards(noteId) {
    try {
        // Call via Storage to avoid the global loadNotes() in notes.js (which returns undefined)
        const notes = Storage.loadNotes();
        if (!Array.isArray(notes)) return [];
        const note = notes.find(n => n.id === noteId);
        return (note && Array.isArray(note.flashcards)) ? note.flashcards : [];
    } catch (error) {
        console.error('Error loading flashcards:', error);
        return [];
    }
}

/**
 * Update the reviewStatus of a single flashcard.
 * @param {string} noteId
 * @param {string} flashcardId
 * @param {'new'|'got-it'|'review-again'} status
 * @returns {boolean}
 */
function updateFlashcardStatus(noteId, flashcardId, status) {
    try {
        const notes = Storage.loadNotes();
        if (!Array.isArray(notes)) return false;
        const note = notes.find(n => n.id === noteId);
        if (!note || !Array.isArray(note.flashcards)) return false;
        const card = note.flashcards.find(c => c.id === flashcardId);
        if (!card) return false;
        card.reviewStatus = status;
        return saveNotes(notes);
    } catch (error) {
        console.error('Error updating flashcard status:', error);
        return false;
    }
}

// ── Deck Storage Functions ─────────────────────────────────────────────────────
// Decks are standalone objects in 'productivity_decks', decoupled from notes.

function loadDecks() {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.DECKS);
        return raw ? _asList(JSON.parse(raw), 'decks') : [];
    } catch (e) {
        console.error('Error loading decks:', e);
        return [];
    }
}

function saveDecks(decks) {
    try {
        localStorage.setItem(STORAGE_KEYS.DECKS, JSON.stringify(decks));
        _checkQuota();
        _cloudPush('decks', decks);
        _announce('decks:changed', { decks });
        return true;
    } catch (e) {
        console.error('Error saving decks:', e);
        return false;
    }
}

function createDeck({ name, description = '', subjectId = null, category = null, sourceNoteId = null, cards = [] }) {
    const decks = loadDecks();
    const now   = new Date().toISOString();
    const deck  = {
        id:           Date.now().toString() + Math.random().toString(36).slice(2, 6),
        name:         name || 'Untitled Deck',
        description,
        subjectId,
        category,
        sourceNoteId,
        cards:        cards.map(c => ({ ...c })),
        createdAt:    now,
        updatedAt:    now
    };
    decks.push(deck);
    saveDecks(decks);
    return deck;
}

function updateDeck(deckId, fields) {
    const decks = loadDecks();
    const deck  = decks.find(d => d.id === deckId);
    if (!deck) return false;
    Object.assign(deck, fields, { updatedAt: new Date().toISOString() });
    return saveDecks(decks);
}

function deleteDeck(deckId) {
    const decks = loadDecks().filter(d => d.id !== deckId);
    return saveDecks(decks);
}

function saveDeckCards(deckId, cardsArray) {
    const decks = loadDecks();
    const deck  = decks.find(d => d.id === deckId);
    if (!deck) return false;
    deck.cards     = cardsArray.map(c => ({ ...c }));
    deck.updatedAt = new Date().toISOString();
    return saveDecks(decks);
}

function getDeckCards(deckId) {
    const deck = loadDecks().find(d => d.id === deckId);
    return (deck && Array.isArray(deck.cards)) ? deck.cards : [];
}

function updateDeckCardStatus(deckId, cardId, status) {
    const decks = loadDecks();
    const deck  = decks.find(d => d.id === deckId);
    if (!deck || !Array.isArray(deck.cards)) return false;
    const card = deck.cards.find(c => c.id === cardId);
    if (!card) return false;
    card.reviewStatus = status;
    deck.updatedAt = new Date().toISOString();
    return saveDecks(decks);
}

function getDeckByNoteId(noteId) {
    return loadDecks().find(d => d.sourceNoteId === noteId) || null;
}

/**
 * Quiz Storage Functions
 */
function saveQuizzes(quizzes) {
    try {
        localStorage.setItem(STORAGE_KEYS.QUIZZES, JSON.stringify(quizzes));
        return true;
    } catch (error) {
        console.error('Error saving quizzes:', error);
        return false;
    }
}

function loadQuizzes() {
    try {
        const quizzes = localStorage.getItem(STORAGE_KEYS.QUIZZES);
        return quizzes ? JSON.parse(quizzes) : [];
    } catch (error) {
        console.error('Error loading quizzes:', error);
        return [];
    }
}


/**
 * Settings Storage Functions
 */
function saveSettings(settings) {
    try {
        localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
        _cloudPush('settings', settings);
        _announce('settings:changed', { settings });
        return true;
    } catch (error) {
        console.error('Error saving settings:', error);
        return false;
    }
}

function loadSettings() {
    try {
        const settings = localStorage.getItem(STORAGE_KEYS.SETTINGS);
        return settings ? JSON.parse(settings) : getDefaultSettings();
    } catch (error) {
        console.error('Error loading settings:', error);
        return getDefaultSettings();
    }
}

function getDefaultSettings() {
    return {
        theme: 'light',
        notifications: true,
        autoSave: true,
        studyReminders: true,
        eventReminders: true,
        aiApiKey: null,
        aiEnabled: false,
        // Notifications
        pomodoroChime: true,
        streakRescue: false,
        // Study preferences
        weekStartsOn: 'monday',
        autoStartPomodoro: true,
        // Meneer behaviour
        meneerProactive: true,
        meneerUseNotes: true,
        meneerShowReasoning: false
    };
}

/**
 * Stats Storage Functions
 */
function saveStats(stats) {
    try {
        localStorage.setItem(STORAGE_KEYS.STATS, JSON.stringify(stats));
        _cloudPush('stats', stats);
        return true;
    } catch (error) {
        console.error('Error saving stats:', error);
        return false;
    }
}

function loadStats() {
    try {
        const stats = localStorage.getItem(STORAGE_KEYS.STATS);
        return stats ? JSON.parse(stats) : getDefaultStats();
    } catch (error) {
        console.error('Error loading stats:', error);
        return getDefaultStats();
    }
}

function getDefaultStats() {
    return {
        totalStudyTime: 0,
        totalQuizzesTaken: 0,
        averageQuizScore: 0,
        notesCreated: 0,
        tasksCompleted: 0,
        totalTimerSessions: 0,
        totalPomodoroSessions: 0,
        lastStudyDate: null
    };
}

/**
 * Study Streak Functions
 */
function saveStudyStreak(streak) {
    try {
        localStorage.setItem(STORAGE_KEYS.STUDY_STREAK, JSON.stringify(streak));
        _cloudPush('studyStreak', streak);
        return true;
    } catch (error) {
        console.error('Error saving study streak:', error);
        return false;
    }
}

function loadStudyStreak() {
    try {
        const streak = localStorage.getItem(STORAGE_KEYS.STUDY_STREAK);
        return streak ? JSON.parse(streak) : { days: 0, lastDate: null };
    } catch (error) {
        console.error('Error loading study streak:', error);
        return { days: 0, lastDate: null };
    }
}

function updateStudyStreak() {
    const streak = loadStudyStreak();
    const today = new Date().toDateString();
    
    if (streak.lastDate === today) {
        return streak.days; // Already updated today
    }
    
    if (streak.lastDate === null) {
        // First time studying
        streak.days = 1;
        streak.lastDate = today;
    } else {
        const lastDate = new Date(streak.lastDate);
        const currentDate = new Date(today);
        const diffTime = currentDate - lastDate;
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) {
            // Consecutive day
            streak.days++;
        } else if (diffDays > 1) {
            // Streak broken
            streak.days = 1;
        }
        streak.lastDate = today;
    }
    
    saveStudyStreak(streak);
    return streak.days;
}

/**
 * Study Time Functions
 */
function saveStudyTime(minutes) {
    try {
        const currentTime = parseInt(localStorage.getItem(STORAGE_KEYS.STUDY_TIME) || '0');
        const newTime = currentTime + minutes;
        localStorage.setItem(STORAGE_KEYS.STUDY_TIME, newTime.toString());
        _cloudPush('studyTime', newTime);
        return true;
    } catch (error) {
        console.error('Error saving study time:', error);
        return false;
    }
}

function loadStudyTime() {
    try {
        return parseInt(localStorage.getItem(STORAGE_KEYS.STUDY_TIME) || '0');
    } catch (error) {
        console.error('Error loading study time:', error);
        return 0;
    }
}

/**
 * Timer Storage Functions
 */
function saveTimers(timers) {
    try {
        localStorage.setItem(STORAGE_KEYS.TIMERS, JSON.stringify(timers));
        _cloudPush('timers', timers);
        return true;
    } catch (error) {
        console.error('Error saving timers:', error);
        return false;
    }
}

function loadTimers() {
    try {
        const timers = localStorage.getItem(STORAGE_KEYS.TIMERS);
        return timers ? JSON.parse(timers) : getDefaultTimers();
    } catch (error) {
        console.error('Error loading timers:', error);
        return getDefaultTimers();
    }
}

function getDefaultTimers() {
    return {
        pomodoro: {
            work: 25,
            break: 5,
            longBreak: 15,
            sessions: 4
        },
        custom: {
            duration: 30,
            name: 'Custom Timer'
        }
    };
}

function saveTimerSessions(sessions) {
    try {
        localStorage.setItem(STORAGE_KEYS.TIMER_SESSIONS, JSON.stringify(sessions));
        _cloudPush('timerSessions', sessions);
        return true;
    } catch (error) {
        console.error('Error saving timer sessions:', error);
        return false;
    }
}

function loadTimerSessions() {
    try {
        const sessions = localStorage.getItem(STORAGE_KEYS.TIMER_SESSIONS);
        return sessions ? JSON.parse(sessions) : [];
    } catch (error) {
        console.error('Error loading timer sessions:', error);
        return [];
    }
}

function addTimerSession(session) {
    try {
        const sessions = loadTimerSessions();
        sessions.push({
            ...session,
            id: Date.now().toString(),
            timestamp: new Date().toISOString()
        });
        return saveTimerSessions(sessions);
    } catch (error) {
        console.error('Error adding timer session:', error);
        return false;
    }
}

/**
 * Custom Presets Storage Functions
 */
function saveCustomPresets(presets) {
    try {
        localStorage.setItem(STORAGE_KEYS.CUSTOM_PRESETS, JSON.stringify(presets));
        _cloudPush('customPresets', presets);
        return true;
    } catch (error) {
        console.error('Error saving custom presets:', error);
        return false;
    }
}

function loadCustomPresets() {
    try {
        const presets = localStorage.getItem(STORAGE_KEYS.CUSTOM_PRESETS);
        return presets ? JSON.parse(presets) : [];
    } catch (error) {
        console.error('Error loading custom presets:', error);
        return [];
    }
}

/**
 * Enhanced Task Storage Functions
 */
function saveTasks(tasks) {
    try {
        localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(tasks));
        _cloudPush('tasks', tasks);
        _announce('tasks:changed', { tasks });
        return true;
    } catch (error) {
        console.error('Error saving tasks:', error);
        return false;
    }
}

function loadTasks() {
    try {
        // Check if STORAGE_KEYS is defined
        if (typeof STORAGE_KEYS === 'undefined') {
            console.error('STORAGE_KEYS is not defined');
            return getDefaultTasks();
        }

        const tasks = localStorage.getItem(STORAGE_KEYS.TASKS);

        if (tasks) {
            const parsedTasks = JSON.parse(tasks);

            // Check if the parsed tasks have the correct structure
            if (parsedTasks && typeof parsedTasks === 'object' && parsedTasks.pending && parsedTasks.completed) {
                // Normalize: ensure every task has subtasks array and parentId
                const normalize = t => ({
                    subtasks: [],
                    parentId: null,
                    ...t,
                    subtasks: Array.isArray(t.subtasks) ? t.subtasks : []
                });
                parsedTasks.pending   = parsedTasks.pending.map(normalize);
                parsedTasks.completed = parsedTasks.completed.map(normalize);
                return parsedTasks;
            } else {
                // Clear corrupted data and return default structure
                localStorage.removeItem(STORAGE_KEYS.TASKS);
                const defaultTasks = getDefaultTasks();
                saveTasks(defaultTasks);
                return defaultTasks;
            }
        } else {
            return getDefaultTasks();
        }
    } catch (error) {
        console.error('Error loading tasks:', error);
        localStorage.removeItem(STORAGE_KEYS.TASKS);
        const defaultTasks = getDefaultTasks();
        saveTasks(defaultTasks);
        return defaultTasks;
    }
}

function getDefaultTasks() {
    return {
        pending: [],
        completed: []
    };
}

function addTask(task) {
    try {
        const tasks = loadTasks();

        const newTask = {
            subtasks: [],
            parentId: null,
            estimatedMinutes: null,
            energy: null,
            ...task,
            id: Date.now().toString(),
            createdAt: new Date().toISOString(),
            completed: false,
            subtasks: Array.isArray(task.subtasks) ? task.subtasks : []
        };
        
        tasks.pending.push(newTask);
        return saveTasks(tasks);
    } catch (error) {
        console.error('Error adding task:', error);
        return false;
    }
}

function completeTask(taskId) {
    try {
        const tasks = loadTasks();
        const taskIndex = tasks.pending.findIndex(t => t.id === taskId);
        if (taskIndex !== -1) {
            const task = tasks.pending.splice(taskIndex, 1)[0];
            task.completed = true;
            task.completedAt = new Date().toISOString();
            tasks.completed.push(task);
            
            // Update stats
            const stats = loadStats();
            stats.tasksCompleted++;
            saveStats(stats);
            
            return saveTasks(tasks);
        }
        return false;
    } catch (error) {
        console.error('Error completing task:', error);
        return false;
    }
}

function updateTask(taskId, updatedTask) {
    try {
        const tasks = loadTasks();
        const taskIndex = tasks.pending.findIndex(t => t.id === taskId);
        if (taskIndex !== -1) {
            tasks.pending[taskIndex] = {
                ...tasks.pending[taskIndex],
                ...updatedTask,
                id: taskId, // Preserve original ID
                createdAt: tasks.pending[taskIndex].createdAt // Preserve original creation date
            };
            return saveTasks(tasks);
        }
        return false;
    } catch (error) {
        console.error('Error updating task:', error);
        return false;
    }
}

function deleteTask(taskId) {
    try {
        const tasks = loadTasks();
        tasks.pending = tasks.pending.filter(t => t.id !== taskId);
        tasks.completed = tasks.completed.filter(t => t.id !== taskId);
        return saveTasks(tasks);
    } catch (error) {
        console.error('Error deleting task:', error);
        return false;
    }
}

/**
 * Overwrite the subtasks array on a task (pending or completed).
 * @param {string} taskId
 * @param {Array}  subtasksArray  — [{id, title, completed, createdAt}]
 */
function saveSubtasks(taskId, subtasksArray) {
    try {
        const tasks = loadTasks();
        const task = tasks.pending.find(t => t.id === taskId)
                  || tasks.completed.find(t => t.id === taskId);
        if (!task) return false;
        task.subtasks = subtasksArray;
        return saveTasks(tasks);
    } catch (error) {
        console.error('Error saving subtasks:', error);
        return false;
    }
}

/**
 * Flip the completed state of a single subtask and persist.
 * @param {string} taskId
 * @param {string} subtaskId
 */
function toggleSubtask(taskId, subtaskId) {
    try {
        const tasks = loadTasks();
        const task = tasks.pending.find(t => t.id === taskId)
                  || tasks.completed.find(t => t.id === taskId);
        if (!task || !Array.isArray(task.subtasks)) return false;
        const subtask = task.subtasks.find(s => s.id === subtaskId);
        if (!subtask) return false;
        subtask.completed = !subtask.completed;
        return saveTasks(tasks);
    } catch (error) {
        console.error('Error toggling subtask:', error);
        return false;
    }
}

// ── Events Storage ─────────────────────────────────────────────────────────────

function saveEvents(events) {
    try {
        localStorage.setItem(STORAGE_KEYS.EVENTS, JSON.stringify(events));
        _cloudPush('events', events);
        _announce('events:changed', { events });
        return true;
    } catch (error) {
        console.error('Error saving events:', error);
        return false;
    }
}

function loadEvents() {
    try {
        const events = localStorage.getItem(STORAGE_KEYS.EVENTS);
        return events ? _asList(JSON.parse(events), 'events') : [];
    } catch (error) {
        console.error('Error loading events:', error);
        return [];
    }
}

function addEvent(event) {
    try {
        const events = loadEvents();
        events.push({
            ...event,
            id: Date.now().toString(),
            createdAt: new Date().toISOString()
        });
        return saveEvents(events);
    } catch (error) {
        console.error('Error adding event:', error);
        return false;
    }
}

function updateEvent(id, updatedEvent) {
    try {
        const events = loadEvents();
        const index = events.findIndex(e => e.id === id);
        if (index !== -1) {
            events[index] = { ...events[index], ...updatedEvent, id };
            return saveEvents(events);
        }
        return false;
    } catch (error) {
        console.error('Error updating event:', error);
        return false;
    }
}

function deleteEvent(id) {
    try {
        const events = loadEvents().filter(e => e.id !== id);
        return saveEvents(events);
    } catch (error) {
        console.error('Error deleting event:', error);
        return false;
    }
}

/**
 * Export/Import Functions
 */
function exportData() {
    try {
        const data = {
            notes: Storage.loadNotes(),
            decks: loadDecks(),
            quizzes: loadQuizzes(),
            tasks: loadTasks(),
            events: loadEvents(),
            timers: loadTimers(),
            timerSessions: loadTimerSessions(),
            customPresets: loadCustomPresets(),
            settings: loadSettings(),
            stats: loadStats(),
            studyStreak: loadStudyStreak(),
            studyTime: loadStudyTime(),
            exportDate: new Date().toISOString(),
            version: '1.3.0'
        };
        
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `studymeneer-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        return true;
    } catch (error) {
        console.error('Error exporting data:', error);
        return false;
    }
}

function importData(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = function(e) {
            try {
                const data = JSON.parse(e.target.result);
                
                // Validate data structure
                if (!data.version || !data.exportDate) {
                    throw new Error('Invalid backup file format');
                }
                
                // Import data
                if (data.notes) saveNotes(data.notes);
                if (data.decks) saveDecks(data.decks);
                if (data.quizzes) saveQuizzes(data.quizzes);
                if (data.tasks) saveTasks(data.tasks);
                if (data.events) saveEvents(data.events);
                if (data.timers) saveTimers(data.timers);
                if (data.timerSessions) saveTimerSessions(data.timerSessions);
                if (data.customPresets) saveCustomPresets(data.customPresets);
                if (data.settings) saveSettings(data.settings);
                if (data.stats) saveStats(data.stats);
                if (data.studyStreak) saveStudyStreak(data.studyStreak);
                if (data.studyTime) localStorage.setItem(STORAGE_KEYS.STUDY_TIME, data.studyTime.toString());
                
                resolve(true);
            } catch (error) {
                console.error('Error importing data:', error);
                reject(error);
            }
        };
        
        reader.onerror = function() {
            reject(new Error('Failed to read file'));
        };
        
        reader.readAsText(file);
    });
}

/**
 * Clear All Data
 */
function _clearAllDataUnlocked() {
    try {
        const keys = [...Object.values(STORAGE_KEYS), ...LEGACY_STORAGE_KEYS];
        keys.forEach(key => {
            localStorage.removeItem(key);
        });
        const remaining = keys.filter(key => localStorage.getItem(key) !== null);
        if (remaining.length) {
            console.error('Error clearing data: some registered storage keys remain.', remaining);
            return false;
        }
        return true;
    } catch (error) {
        console.error('Error clearing data:', error);
        return false;
    }
}

async function clearAllData() {
    const locks = globalThis.navigator?.locks;
    if (!locks || typeof locks.request !== 'function') {
        return _clearAllDataUnlocked();
    }

    try {
        return await locks.request(
            PENDING_QUIZ_QUEUE_LOCK_NAME,
            { mode: 'exclusive' },
            lock => {
                if (!lock) {
                    console.error('Error clearing data: the quiz queue lock was not granted.');
                    return false;
                }
                return _clearAllDataUnlocked();
            }
        );
    } catch (error) {
        console.error('Error clearing data: could not acquire the quiz queue lock.', error);
        return false;
    }
}

/**
 * Storage Health Check
 */
function checkStorageHealth() {
    try {
        const testKey = 'storage_test';
        const testValue = 'test_data';
        
        localStorage.setItem(testKey, testValue);
        const retrieved = localStorage.getItem(testKey);
        localStorage.removeItem(testKey);
        
        return retrieved === testValue;
    } catch (error) {
        console.error('Storage health check failed:', error);
        return false;
    }
}

/**
 * Get Storage Usage
 */
function getStorageUsage() {
    try {
        let totalSize = 0;
        Object.values(STORAGE_KEYS).forEach(key => {
            const value = localStorage.getItem(key);
            if (value) {
                totalSize += new Blob([value]).size;
            }
        });
        
        return {
            used: totalSize,
            usedKB: (totalSize / 1024).toFixed(2),
            usedMB: (totalSize / (1024 * 1024)).toFixed(4),
            limit: 5 * 1024 * 1024, // 5MB typical localStorage limit
            percentage: ((totalSize / (5 * 1024 * 1024)) * 100).toFixed(2)
        };
    } catch (error) {
        console.error('Error calculating storage usage:', error);
        return null;
    }
}

/**
 * Custom Quiz Storage Functions
 */
function saveCustomQuizzes(quizzes) {
    try {
        localStorage.setItem(STORAGE_KEYS.CUSTOM_QUIZZES, JSON.stringify(quizzes));
        _cloudPush('quizzes', quizzes);
        return true;
    } catch (error) {
        console.error('Error saving custom quizzes:', error);
        return false;
    }
}

function loadCustomQuizzes() {
    try {
        const quizzes = localStorage.getItem(STORAGE_KEYS.CUSTOM_QUIZZES);
        return quizzes ? JSON.parse(quizzes) : [];
    } catch (error) {
        console.error('Error loading custom quizzes:', error);
        return [];
    }
}

/**
 * Quiz Results Storage Functions
 */
function saveQuizResults(results) {
    try {
        localStorage.setItem(STORAGE_KEYS.QUIZ_RESULTS, JSON.stringify(results));
        _cloudPush('quizResults', results);
        return true;
    } catch (error) {
        console.error('Error saving quiz results:', error);
        return false;
    }
}

function loadQuizResults() {
    try {
        const results = localStorage.getItem(STORAGE_KEYS.QUIZ_RESULTS);
        return results ? JSON.parse(results) : [];
    } catch (error) {
        console.error('Error loading quiz results:', error);
        return [];
    }
}

function addQuizResult(result) {
    try {
        const results = loadQuizResults();
        results.push({
            ...result,
            id: Date.now().toString(),
            timestamp: new Date().toISOString()
        });
        return saveQuizResults(results);
    } catch (error) {
        console.error('Error adding quiz result:', error);
        return false;
    }
}

/**
 * Mistake Log Storage Functions
 */
function saveMistakeLog(mistakeLog) {
    try {
        localStorage.setItem(STORAGE_KEYS.MISTAKE_LOG, JSON.stringify(mistakeLog));
        _cloudPush('mistakeLog', mistakeLog);
        return true;
    } catch (error) {
        console.error('Error saving mistake log:', error);
        return false;
    }
}

function loadMistakeLog() {
    try {
        const mistakeLog = localStorage.getItem(STORAGE_KEYS.MISTAKE_LOG);
        return mistakeLog ? JSON.parse(mistakeLog) : {};
    } catch (error) {
        console.error('Error loading mistake log:', error);
        return {};
    }
}

function addMistake(quizId, questionId) {
    try {
        const mistakeLog = loadMistakeLog();
        if (!mistakeLog[quizId]) {
            mistakeLog[quizId] = [];
        }
        if (!mistakeLog[quizId].includes(questionId)) {
            mistakeLog[quizId].push(questionId);
        }
        return saveMistakeLog(mistakeLog);
    } catch (error) {
        console.error('Error adding mistake:', error);
        return false;
    }
}

function getMistakesForQuiz(quizId) {
    try {
        const mistakeLog = loadMistakeLog();
        return mistakeLog[quizId] || [];
    } catch (error) {
        console.error('Error getting mistakes for quiz:', error);
        return [];
    }
}

/**
 * AI Quiz Storage Functions
 */
function savePendingQuizRequests(requests) {
    try {
        localStorage.setItem(STORAGE_KEYS.PENDING_QUIZ_REQUESTS, JSON.stringify(requests));
        return true;
    } catch (error) {
        console.error('Error saving pending quiz requests:', error);
        return false;
    }
}

function loadPendingQuizRequests() {
    try {
        const requests = localStorage.getItem(STORAGE_KEYS.PENDING_QUIZ_REQUESTS);
        return requests ? JSON.parse(requests) : [];
    } catch (error) {
        console.error('Error loading pending quiz requests:', error);
        return [];
    }
}

// Current code uses an explicit legacy name so a call site cannot accidentally
// make the old queue part of automatic replay again.
function loadLegacyPendingQuizRequests() {
    try {
        const requests = localStorage.getItem(STORAGE_KEYS.PENDING_QUIZ_REQUESTS);
        return requests ? JSON.parse(requests) : [];
    } catch (error) {
        console.error('Error loading legacy pending quiz requests:', error);
        return null;
    }
}

function savePendingQuizRequestsV2(requests) {
    try {
        localStorage.setItem(STORAGE_KEYS.PENDING_QUIZ_REQUESTS_V2, JSON.stringify(requests));
        return true;
    } catch (error) {
        console.error('Error saving v2 pending quiz requests:', error);
        return false;
    }
}

function loadPendingQuizRequestsV2() {
    try {
        const requests = localStorage.getItem(STORAGE_KEYS.PENDING_QUIZ_REQUESTS_V2);
        return requests ? JSON.parse(requests) : [];
    } catch (error) {
        console.error('Error loading v2 pending quiz requests:', error);
        return null;
    }
}


// Export functions for use in other modules
export const Storage = {
    // Notes
    saveNotes,
    loadNotes,
    
    // Quizzes
    saveQuizzes,
    loadQuizzes,
    
    // Custom Quizzes
    saveCustomQuizzes,
    loadCustomQuizzes,
    
    // Quiz Results
    saveQuizResults,
    loadQuizResults,
    addQuizResult,
    
    // Mistake Log
    saveMistakeLog,
    loadMistakeLog,
    addMistake,
    getMistakesForQuiz,
    
    // Tasks
    saveTasks,
    loadTasks,
    addTask,
    completeTask,
    updateTask,
    deleteTask,
    saveSubtasks,
    toggleSubtask,
    
    // Timers
    saveTimers,
    loadTimers,
    saveTimerSessions,
    loadTimerSessions,
    addTimerSession,
    
    // Custom Presets
    saveCustomPresets,
    loadCustomPresets,
    
    // Settings
    saveSettings,
    loadSettings,
    
    // Stats
    saveStats,
    loadStats,
    
    // Study Streak
    saveStudyStreak,
    loadStudyStreak,
    updateStudyStreak,
    
    // Study Time
    saveStudyTime,
    loadStudyTime,
    
    // Events
    saveEvents,
    loadEvents,
    addEvent,
    updateEvent,
    deleteEvent,

    // Export/Import
    exportData,
    importData,
    
    // Pending Quiz Requests (offline queue)
    savePendingQuizRequests,
    loadPendingQuizRequests,
    loadLegacyPendingQuizRequests,
    savePendingQuizRequestsV2,
    loadPendingQuizRequestsV2,

    // Flashcards (note-embedded, legacy)
    saveFlashcards,
    getFlashcards,
    updateFlashcardStatus,

    // Decks (standalone flashcard decks)
    loadDecks,
    saveDecks,
    createDeck,
    updateDeck,
    deleteDeck,
    saveDeckCards,
    getDeckCards,
    updateDeckCardStatus,
    getDeckByNoteId,

    // Utilities
    clearAllData,
    checkStorageHealth,
    getStorageUsage
};

export default Storage;

// Transition shim (AGENTS.md SKILL 1) — the namespace object ONLY. See the header:
// publishing the individual functions here would invert nine name collisions.
if (typeof window !== 'undefined') window.Storage = Storage;
